import type { Filter, FilterCondition } from '@crm/contracts';
import { andFilter } from './list-view.ts';

/**
 * The Projects list's "Mine" quick filter, kept INSIDE the view's filter like the Jobs list's
 * (RELAY-1038) so a saved view, a link, the remembered view and every layout (table, board, each
 * Multi board lane) carry it. Mine is "the project's owner is me": one `ownerId eq <me>`
 * condition. `splitProjectFilter` lifts exactly that condition out of a stored filter, so the
 * filter bar's Owner chip shows only owners someone chose there, never a second copy of the
 * toggle; `joinProjectFilter` puts it back first.
 */
export function mineProjectFilter(userId: string): FilterCondition {
  return { field: 'ownerId', op: 'eq', value: userId };
}

const isMine = (f: Filter, userId: string) =>
  'field' in f && f.field === 'ownerId' && f.op === 'eq' && f.value === userId;

export interface ProjectQuickFilters {
  mine: boolean;
  /** What the filter bar shows and edits. */
  conditions: FilterCondition[];
  /** Anything else (a group from someone else's link): kept, applied, not shown. */
  rest: Filter[];
}

export function splitProjectFilter(
  filter: Filter | undefined,
  userId: string,
): ProjectQuickFilters {
  const parts = !filter ? [] : 'and' in filter ? filter.and : [filter];
  const out: ProjectQuickFilters = { mine: false, conditions: [], rest: [] };
  for (const p of parts) {
    if (isMine(p, userId)) out.mine = true;
    else if ('field' in p) out.conditions.push(p);
    else out.rest.push(p);
  }
  return out;
}

export function joinProjectFilter(q: ProjectQuickFilters, userId: string): Filter | undefined {
  return andFilter([...(q.mine ? [mineProjectFilter(userId)] : []), ...q.conditions, ...q.rest]);
}
